/**
 * Template Processor — shared between CWHardwareController and Template Designer.
 *
 * Extracted from CWHardwareController.js to avoid code duplication.
 * Processes receipt template JSON with data placeholders.
 */

export const getDeepValue = (obj, path) => {
  return path.split(".").reduce((acc, part) => (acc && acc[part] !== undefined ? acc[part] : null), obj);
};

export const formatValue = (value, format, column) => {
  let formatted = value;

  if (format && format === "currency" && typeof value === "number") {
    formatted = value.toFixed(2);
  }

  if (column?.trim && typeof formatted === "string" && column?.width) {
    formatted = formatted.slice(0, column.width);
  }

  return formatted;
};

export const replacePlaceholders = (text, data) => {
  return text.replace(/{{([^}]+)}}/g, (match, placeholder) => {
    // Handle currentDateTime
    if (placeholder === "currentDateTime") {
      const d = new Date();

      const day = String(d.getDate()).padStart(2, "0");
      const month = String(d.getMonth() + 1).padStart(2, "0");
      const year = d.getFullYear();

      let hours = d.getHours();
      const minutes = String(d.getMinutes()).padStart(2, "0");
      const seconds = String(d.getSeconds()).padStart(2, "0");

      const ampm = hours >= 12 ? "PM" : "AM";
      hours = hours % 12 || 12; // convert 0 → 12

      return `${day}/${month}/${year}, ${hours}:${minutes}:${seconds} ${ampm}`;
    }

    // Get the value from the data object (for simple placeholders like {{tax}})
    let value = getDeepValue(data, placeholder);

    // Format numbers correctly for simple placeholders
    if (typeof value === "number" && !isNaN(value)) {
      return value === 0 ? "0.00" : value.toFixed(2);
    }

    // Handle array sum
    if (placeholder.startsWith("sum ")) {
      try {
        const [_, path] = placeholder.split("sum ").map((s) => s.trim());
        const array = getDeepValue(data, path.split(".")[0]);
        const key = path.split(".")[1];

        if (Array.isArray(array) && key) {
          const sum = array.reduce((total, item) => {
            const val = typeof item[key] === "string" ? parseFloat(item[key]) : item[key];
            return total + (typeof val === "number" && !isNaN(val) ? val : 0);
          }, 0);
          return sum === 0 ? "0.00" : sum.toFixed(2);
        }
        return "0.00";
      } catch (error) {
        console.error("Error summing array key:", placeholder, error);
        return "0.00";
      }
    }

    // Handle expressions like {{tax / 2}}
    if (placeholder.match(/[+\-*/]/)) {
      try {
        const resolvedExpression = placeholder.replace(/\b[a-zA-Z_]\w*\b/g, (varName) => {
          const val = getDeepValue(data, varName);
          const numVal = typeof val === "string" ? parseFloat(val) : val;
          return typeof numVal === "number" && !isNaN(numVal) ? numVal : 0;
        });
        const result = eval(resolvedExpression);
        if (!isFinite(result)) {
          return "0.00";
        }
        return result === 0 ? "0.00" : result.toFixed(2);
      } catch (error) {
        console.error("Error evaluating expression:", placeholder, error);
        return "0.00";
      }
    }

    // Return original value or empty string if undefined/null
    return value !== null && value !== undefined ? value : "";
  });
};

export const processReceiptTemplate = (template, data) => {
  const processedTemplate = JSON.parse(JSON.stringify(template));

  processedTemplate.elements = processedTemplate.elements.flatMap((element) => {
    // Process text elements
    if (element.type === "text" && element.content && typeof element.content === "string") {
      element.content = replacePlaceholders(element.content, data);
    }

    // Process table elements
    if (element.type === "table" && element.data && typeof element.data === "string" && element.dataMapping) {
      const dataPath = element.data.replace(/{{|}}/g, "");
      const tableData = getDeepValue(data, dataPath) || [];

      const columnKeys = element.columns.map((col, idx) => ({
        key: Object.keys(element.dataMapping).find((k) => element.dataMapping[k] === Object.values(element.dataMapping)[idx]),
        width: col.width,
        alignment: col.alignment,
        format: col.format,
        trim: col?.trim === true,
      }));

      element.data = (Array.isArray(tableData) ? tableData : [])
        .flatMap((item) => {
          const hasChildren = item[element.GroupedItems?.field] && Array.isArray(item[element.GroupedItems?.field]) && item[element.GroupedItems?.field].length > 0;
          const mainRow = !hasChildren
            ? {
                ...Object.keys(element.dataMapping).reduce((row, targetKey) => {
                  const colMeta = columnKeys.find((ck) => ck.key === targetKey);
                  row[targetKey] = formatValue(item[element.dataMapping[targetKey]], colMeta?.format, colMeta);
                  return row;
                }, {}),
              }
            : null;

          const extraRows = (element.extraRows || []).flatMap((extra) => {
            if (extra.fields && Array.isArray(extra.fields)) {
              const allHidden = extra.fields.every((fieldConfig) => {
                const value = item[fieldConfig.field];
                return fieldConfig.hideIfZeroOrEmpty && (value === null || value === undefined || value === 0);
              });

              if (allHidden) {
                return [];
              }

              const extraRow = {};
              let hasContent = false;
              columnKeys.forEach((col) => {
                const fieldConfig = extra.fields.find((f) => f.column === col.key);
                if (fieldConfig) {
                  const value = item[fieldConfig.field];
                  const shouldHide = fieldConfig.hideIfZeroOrEmpty && (value === null || value === undefined || value === 0);
                  if (!shouldHide && value !== null && value !== undefined) {
                    extraRow[col.key] = (fieldConfig.prefix || "") + (fieldConfig.format || fieldConfig.trim ? formatValue(value, fieldConfig?.format, fieldConfig) : value);
                    hasContent = true;
                  } else {
                    extraRow[col.key] = "";
                  }
                } else {
                  extraRow[col.key] = "";
                }
              });

              return hasContent ? [extraRow] : [];
            }

            const value = item[extra.field];
            if (extra.loop) {
              const loopData = value || [];
              if (!Array.isArray(loopData)) {
                return [];
              }
              return loopData.flatMap((loopItem) => {
                // NEW ADDON TABLE ROW SUPPORT
                if (extra.type === "tableRow" && extra.dataMapping) {
                  const addonRow = {};

                  columnKeys.forEach((col) => {
                    const mappedField = extra.dataMapping[col.key];

                    addonRow[col.key] = mappedField && loopItem[mappedField] !== undefined ? formatValue(loopItem[mappedField], col.format, col) : "";
                  });

                  return [addonRow];
                }
              });
            }

            const shouldHide = extra.hideIfZeroOrEmpty && (value === null || value === undefined || value === 0);
            if (!shouldHide && value !== null && value !== undefined) {
              const extraRow = {};
              columnKeys.forEach((col) => {
                extraRow[col.key] = col.key === extra.column ? value : "";
                if ((extra.format || extra.trim) && col.key === extra.column) {
                  extraRow[col.key] = formatValue(value, extra?.format, extra);
                }
              });
              return [extraRow];
            }
            return [];
          });

          const groupedRows = [];
          if (element.GroupedItems) {
            const groupedConfig = element.GroupedItems;
            const value = item[groupedConfig.field];
            if (groupedConfig.loop && Array.isArray(value) && value.length > 0) {
              if (groupedConfig.boldParent && item[groupedConfig.subField]) {
                const parentRow = {};
                columnKeys.forEach((col) => {
                  parentRow[col.key] = col.key === Object.keys(element.dataMapping)[0] ? item[groupedConfig.subField] : "";
                });
                parentRow.style = ["bold"];
                groupedRows.push(parentRow);
              }

              value.forEach((loopItem) => {
                const subValue = loopItem[groupedConfig.subField];
                const shouldHide = groupedConfig.hideIfZeroOrEmpty && (subValue === null || subValue === undefined || subValue === "");
                if (!shouldHide && subValue !== null && subValue !== undefined) {
                  const groupedRow = {};
                  columnKeys.forEach((col) => {
                    if (groupedConfig.dataMapping && groupedConfig.dataMapping[col.key]) {
                      groupedRow[col.key] = formatValue(
                        loopItem[groupedConfig.dataMapping[col.key]],
                        col.format || (groupedConfig.format && col.key === groupedConfig.column ? groupedConfig.format : undefined),
                        col
                      );
                    } else {
                      groupedRow[col.key] = col.key === groupedConfig.column ? (groupedConfig.prefix || "") + subValue : "";
                    }
                  });
                  groupedRows.push(groupedRow);
                }
              });
            }
          }

          return mainRow ? [mainRow, ...extraRows, ...groupedRows] : [...extraRows, ...groupedRows];
        })
        .filter((row) => {
          return Object.values(row).some((val) => val !== "" && val !== null && val !== undefined);
        });
    }

    // Process barcode elements
    if (element.type === "barcode" && element.content) {
      element.content = replacePlaceholders(element.content, data);
    }

    // Process QR elements
    if (element.type === "qrcode" && element.content) {
      element.content = replacePlaceholders(element.content, data);
    }

    // Process loop elements
    if (element.type === "loop" && element.data && typeof element.data === "string") {
      const loopDataPath = element.data.replace(/{{|}}/g, "");
      const loopData = getDeepValue(data, loopDataPath) || [];
      if (Array.isArray(loopData)) {
        return loopData.flatMap((loopItem) => {
          return element.elements.map((loopElement) => {
            const newElement = JSON.parse(JSON.stringify(loopElement));
            if (newElement.type === "text" && newElement.content && typeof newElement.content === "string") {
              newElement.content = replacePlaceholders(newElement.content, loopItem);
            }
            if (newElement.type === "table" && newElement.data && typeof newElement.data === "string" && newElement.dataMapping) {
              const tableData = getDeepValue(loopItem, newElement.data.replace(/{{|}}/g, "")) || [];
              const columnKeys = newElement.columns.map((col, idx) => ({
                key: Object.keys(newElement.dataMapping).find((k) => newElement.dataMapping[k] === Object.values(newElement.dataMapping)[idx]),
                width: col.width,
                alignment: col.alignment,
                format: col.format,
              }));

              newElement.data = tableData
                .flatMap((item) => {
                  const mainRow = {};
                  for (const targetKey in newElement.dataMapping) {
                    mainRow[targetKey] = formatValue(
                      item[newElement.dataMapping[targetKey]],
                      columnKeys.find((ck) => ck.key === targetKey)?.format,
                      columnKeys.find((ck) => ck.key === targetKey)
                    );
                  }
                  return [mainRow];
                })
                .filter((row) => Object.values(row).some((val) => val !== "" && val !== null && val !== undefined));
            }
            return newElement;
          });
        });
      }
    }

    // Process conditional elements
    if (element.type === "conditional") {
      const condition = element.condition.replace(/{{|}}/g, "").trim();
      try {
        const conditionResult = new Function("data", `with(data){ return ${condition}; }`)(data);
        if (conditionResult) {
          return element.elements.map((el) => processReceiptTemplate({ elements: [el] }, data).elements).flat();
        }
        return [];
      } catch (error) {
        // Undefined template variables evaluate to falsy — skip the conditional block
        return [];
      }
    }

    return element;
  });

  return processedTemplate;
};
